<template lang="pug">
.family-table-component
  // テーブル
  b-table(
    :items="_value"
    :fields="fields"
    :current-page="currentPage"
    :per-page="perPage"
    :filter="filter"
    :busy="isLoading"
    sticky-header
    no-border-collapse
    table-class="basic-table"
    show-empty
    :filter-function="filterTable"
    @filtered="onFiltered"
    :sort-compare="sortCompare"
  )
    // ローディング
    template(#table-busy)
      .text-center.table-loading-message
        b-spinner.spinner
        span.message.align-self-center 読込み中...
    // レコード無し
    template(#empty)
      .empty-message
        span 情報がありません
    // フィルター後レコード無し
    template(#emptyfiltered)
      .empty-message
        span 情報がありません

    // ヘッダー：役割
    template(#head(familyPosition)="data")
      .d-flex.flex-row.justify-content-center
        span.position-name 役割
        HelpIcon(:title="'役割の変更方法について'" :text="'登録情報の修正から、キーパーソンなどの役割を設定できます。設定内容はご家族には公開されません。'")

    // セル：送信手段
    template(#cell(senderType)="row") 
      span {{ getSenderType(row.item.senderType) }}
    // セル：家族からの連絡
    template(#cell(familyReplyFunction)="row")
      template(v-if="row.item.senderType == 1")
        span －
      template(v-else)
        span.used(v-if="row.item.familyReplyFunction == true") 使用可
        span.unused(v-else) 使用不可
    // セル：写真印刷依頼
    template(#cell(familyPictureFunction)="row")
      template(v-if="row.item.senderType == 1")
        span －
      template(v-else)
        span.used(v-if="row.item.familyPictureFunction == true") 使用可
        span.unused(v-else) 使用不可
    // セル：登録情報
    template(#cell(operation)="row")
      .control-buttons(v-if="row.item.senderType == 0")
        button.btn.edit-btn(@click="onEdit(row.item)" type="button") 修正
        button.btn.delete-btn(@click="onLineDelete(row.item)" type="button") 削除
      .control-buttons(v-if="row.item.senderType == 1")
        button.btn.edit-btn(@click="onEdit(row.item)" type="button") 修正
        button.btn.delete-btn(@click="onMailDelete(row.item)" type="button") 削除
    // セル：登録確認
    template(#cell(sendMessage)="row")
      button.btn.testmail-btn(v-if="row.item.senderType == 1" @click="onSendMessage(row.item)" type="button") 確認送信
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";
import { SenderType } from "@/commons/models/Resident";

import HelpIcon from "@/pc/basics/HelpIcon.vue";

/**
 * 家族テーブルコンポーネント
 */
@Component({
  components: {
    HelpIcon
  }
})
export default class FamilyTable extends Vue {
  /** 1行あたりの情報配列 */
  @Prop({ required: true })
  value!: Array<FamilyTableItem>;
  /** 列名配列 */
  @Prop({ required: true })
  fields!: Array<FamilyTableField>;
  /** 全体行数 */
  @Prop({ required: true })
  totalRows!: number;
  /** 現在のページ */
  @Prop({ required: true })
  currentPage!: number;
  /** 1ページあたりの表示行数 */
  @Prop({ required: true })
  perPage!: number;
  /** ローディング表示可否 */
  @Prop({ required: true })
  isLoading!: boolean;
  /** フィルター */
  @Prop()
  filter!: FilterSetting;

  get _value(): Array<FamilyTableItem> {
    return this.value;
  }

  /**
   * ### 送信種別を返す
   */
  public getSenderType(senderType: SenderType): string {
    if (senderType == SenderType.LINE) {
      return "LINE";
    } else if (senderType == SenderType.MAIL) {
      return "メール";
    } else {
      return "";
    }
  }

  /**
   * ### フィルタリング
   */
  public filterTable(row: FamilyTableItem, filter: FilterSetting): boolean {
    return this.filterGroupId(row, filter) && this.filterSearchWords(row, filter);
  }

  public filterGroupId(row: FamilyTableItem, filter: FilterSetting): boolean {
    if (filter.id == "") {
      return true;
    }

    if (row.groupId == Number(filter.id)) {
      return true;
    } else {
      return false;
    }
  }

  public filterSearchWords(row: FamilyTableItem, filter: FilterSetting): boolean {
    if (filter.searchWord == "") {
      return true;
    }

    const words: string[] = this.$filterUtil.createSearchWord(filter.searchWord);

    let foundResidentName: boolean = true;
    let foundResidentKanaName: boolean = true;
    let foundRoom: boolean = true;
    let foundFamilyName: boolean = true;
    let foundFamilyRelation: boolean = true;
    let foundFamilyPosition: boolean = true;

    for (const word of words) {
      // 利用者名
      if (row.residentName.indexOf(word) == -1) {
        foundResidentName = false;
      }

      // ふりがなが登録されている場合は文字列を含むかチェックする
      if (row.residentKanaName) {
        if (row.residentKanaName.indexOf(word) == -1) {
          foundResidentKanaName = false;
        }
      }
      // ふりがなが登録されていない場合は対象外として扱う
      else {
        foundResidentKanaName = false;
      }

      // 部屋名
      if (row.room.indexOf(word) == -1) {
        foundRoom = false;
      }

      // 家族名
      if (row.familyName.indexOf(word) == -1) {
        foundFamilyName = false;
      }

      // 家族続柄
      if (row.familyRelation.indexOf(word) == -1) {
        foundFamilyRelation = false;
      }

      // 家族役割
      if (!row.familyPosition || row.familyPosition.indexOf(word) == -1) {
        foundFamilyPosition = false;
      }
    }

    return foundResidentName || foundResidentKanaName || foundRoom || foundFamilyName || foundFamilyRelation || foundFamilyPosition;
  }

  /**
   * ### フィルタリングイベント
   */
  public onFiltered(filteredItems: any) {
    this.$emit("update:totalRows", filteredItems.length);
    return;
  }

  /**
   * ### カスタムソート処理
   *
   * 指定した列のみ特別なソート（比較）ロジックを適用します。
   *
   * @param a - 比較対象の行データ1
   * @param b - 比較対象の行データ2
   * @param key - ソート対象の列キー
   */
  sortCompare(a: FamilyTableItem, b: FamilyTableItem, key: string): number | boolean {
    // 特別なソートをしたい場合はここで記述する
    switch (key) {
      // 利用者名
      case "residentName": {
        // ふりがな(kanaName)が両方存在する場合はふりがなでソートする
        if (a.residentKanaName && b.residentKanaName) {
          return a.residentKanaName.localeCompare(b.residentKanaName, "ja");
        }

        // ふりがなが存在するものを優先してソートする
        if (a.residentKanaName && !b.residentKanaName) return -1;
        if (!a.residentKanaName && b.residentKanaName) return 1;

        // ふりがなが無いもの同士はnameでソートする
        const nameA = a.residentName || "";
        const nameB = b.residentName || "";
        return nameA.localeCompare(nameB, "ja");
      }
      // その他
      // falseを返すことで、name以外の列は標準のソート（文字列/数値比較）に任せる
      default:
        return false;
    }
  }

  /**
   * ### 修正ボタン押下イベント
   */
  public onEdit(family: FamilyTableItem): void {
    this.$emit("onEdit", family);
    return;
  }

  /**
   * ### LINEユーザー削除ボタン押下イベント
   */
  public onLineDelete(family: FamilyTableItem): void {
    this.$emit("onLineDelete", family);
    return;
  }

  /**
   * ### MAILユーザー削除ボタン押下イベント
   */
  public onMailDelete(family: FamilyTableItem): void {
    this.$emit("onMailDelete", family);
    return;
  }

  /**
   * ### 確認送信ボタン押下イベント
   */
  public onSendMessage(family: FamilyTableItem): void {
    this.$emit("onSendMessage", family);
    return;
  }
}

/** テーブルの列名の情報 */
export interface FamilyTableField {
  key: string;
  label: string;
  sortable: boolean;
  sortDirection: string;
  class?: string;
}

/** テーブルの1行あたりの情報 */
export interface FamilyTableItem {
  id: number;
  residentsSortKey: string;
  residentName: string;
  residentKanaName: string;
  room: string;
  groupId: number;
  groupName: string;
  familyId: number;
  familyName: string;
  familyRelation: string;
  familyPosition: string;
  familyMailaddress: string;
  familyReplyFunction: boolean;
  familyPictureFunction: boolean;
  senderType: SenderType;
  updated: number;
}

export interface FilterSetting {
  id: string;
  searchWord: string;
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

.used {
  color: $font-key-color;
  font-weight: bold;
}
.unused {
  color: $font-disable-font-color;
}

.family-table-component {
  .edit-btn,
  .delete-btn {
    width: 64px;
  }

  .edit-btn {
    margin-right: 12px;
  }

  .testmail-btn {
    width: 100px;
  }

  .position-name {
    margin-right: 4px;
  }
}
</style>
